/**
 * Removed Manga Service
 * Hybrid approach: stores locally in localStorage AND syncs to server when authenticated.
 * Server is the source of truth when available; localStorage is the fallback/cache.
 * Mirrors the pattern from removedAnimeService.js but for manga.
 */

import {
    getRemovedMangaFromServer,
    addRemovedMangaOnServer,
    removeRemovedMangaOnServer,
    batchAddRemovedMangaOnServer,
    clearAllRemovedMangaOnServer
} from './apiService';
import { waitForServerAuth } from './authReadyService';

const STORAGE_KEY = 'removed_manga_ids';
let cachedIds = null; // In-memory cache for sync access

/**
 * Check if user is authenticated (has a token)
 */
const isAuthenticated = () => {
    const { hasToken } = require('./tokenStorage');
    return hasToken();
};

/**
 * Save IDs to localStorage (local cache)
 */
const persistLocal = (ids) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
    } catch (error) {
        console.error('Error saving removed manga to localStorage:', error);
    }
};

/**
 * Load IDs from localStorage
 */
const loadLocal = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            return new Set(JSON.parse(stored));
        }
    } catch (error) {
        console.error('Error loading removed manga from localStorage:', error);
    }
    return new Set();
};

/**
 * Initialize and get removed manga IDs (async)
 * Merges server + local data so nothing is lost.
 * Pushes any local-only IDs up to the server for permanent storage.
 * @returns {Promise<Set<number>>} Set of removed manga IDs
 */
export const initRemovedMangaIds = async () => {
    // Always load local IDs first
    const localIds = loadLocal();

    if (isAuthenticated()) {
        try {
            await waitForServerAuth(); // Wait for server login to complete
            const serverIds = await getRemovedMangaFromServer();
            const serverSet = new Set(serverIds || []);

            // Merge: union of server + local
            const merged = new Set([...localIds, ...serverSet]);

            // Find IDs that exist locally but not on server — push them up
            const localOnly = [...localIds].filter(id => !serverSet.has(id));
            if (localOnly.length > 0) {
                console.log(`📤 Syncing ${localOnly.length} local-only removed manga to server...`);
                batchAddRemovedMangaOnServer(localOnly).catch(err =>
                    console.warn('⚠️ Failed to sync local removed manga to server:', err.message)
                );
            }

            // Save merged set locally
            persistLocal(merged);
            cachedIds = merged;
            console.log(`📡 Removed manga synced: ${merged.size} total (${localOnly.length} pushed to server)`);
            return cachedIds;
        } catch (error) {
            console.warn('⚠️ Could not load removed manga from server, using local:', error.message);
        }
    }

    // Fallback to localStorage only
    cachedIds = localIds;
    return cachedIds;
};

// Initialize immediately
initRemovedMangaIds();

/**
 * Get the current set of removed manga IDs (sync)
 * @returns {Set<number>} Set of removed manga IDs
 */
export const getRemovedMangaIds = () => {
    if (cachedIds !== null) {
        return new Set(cachedIds);
    }
    return loadLocal();
};

/**
 * Add a manga ID to the removed list
 * Saves locally AND to server
 * @param {number} mangaId - The manga ID to remove
 * @returns {Promise<boolean>} Success status
 */
export const addRemovedManga = async (mangaId) => {
    try {
        // Update cache + local
        if (cachedIds) {
            cachedIds = new Set([...cachedIds, mangaId]);
        } else {
            cachedIds = new Set([mangaId]);
        }
        persistLocal(cachedIds);

        // Notify listeners
        window.dispatchEvent(new Event('removedMangaChanged'));

        // Sync to server (fire and forget)
        if (isAuthenticated()) {
            addRemovedMangaOnServer(mangaId).catch(err =>
                console.warn('Server sync failed for removed manga:', err.message)
            );
        }

        return true;
    } catch (error) {
        console.error('Error adding removed manga:', error);
        return false;
    }
};

/**
 * Remove a manga ID from the removed list (restore it)
 * Restores locally AND on server
 * @param {number} mangaId - The manga ID to restore
 * @returns {Promise<boolean>} Success status
 */
export const removeRemovedManga = async (mangaId) => {
    try {
        // Update cache + local
        if (cachedIds) {
            cachedIds = new Set([...cachedIds].filter(id => id !== mangaId));
        }
        persistLocal(cachedIds || new Set());

        // Notify listeners
        window.dispatchEvent(new Event('removedMangaChanged'));

        // Sync to server (fire and forget)
        if (isAuthenticated()) {
            removeRemovedMangaOnServer(mangaId).catch(err =>
                console.warn('Server sync failed for restoring manga:', err.message)
            );
        }

        return true;
    } catch (error) {
        console.error('Error removing manga from removed list:', error);
        return false;
    }
};

/**
 * Check if a manga is removed
 * @param {number} mangaId - The manga ID to check
 * @returns {boolean} True if manga is removed
 */
export const isMangaRemoved = (mangaId) => {
    const removedIds = getRemovedMangaIds();
    return removedIds.has(mangaId);
};

/**
 * Get count of removed manga
 * @returns {number} Number of removed manga
 */
export const getRemovedMangaCount = () => {
    return getRemovedMangaIds().size;
};

/**
 * Clear all removed manga
 * @returns {Promise<boolean>} Success status
 */
export const clearAllRemovedManga = async () => {
    try {
        cachedIds = new Set();
        persistLocal(cachedIds);
        window.dispatchEvent(new Event('removedMangaChanged'));

        // Sync to server
        if (isAuthenticated()) {
            clearAllRemovedMangaOnServer().catch(err =>
                console.warn('Server sync failed for clearing manga:', err.message)
            );
        }

        return true;
    } catch (error) {
        console.error('Error clearing removed manga:', error);
        return false;
    }
};
